import { useEffect, useState } from "react";
import { formatDMY } from "../../../../utils/dateFormat";
import BaseModal from "../../../../components/BaseComponents/BaseModal/BaseModal";
import styles from "./OperationalFlightModal.module.css";
import { useFlightsOnOperationalFlight } from "../../../../api/products/useScheduleUpdaterApi";
import { AlertTriangle, Plane, Users } from "lucide-react";

/* ══════════════════════════════════════════════════════════════════════════
   Find All Bookings On This Flight (§12, §66)

   An airline schedule change affects every SUNSKY traveller booked on that
   operational flight — not just the booking the operator happened to open.
   §66 is explicit that the backend must offer both "Update This Booking Only"
   and "Find & Update All Bookings on This Flight", so an administrator cannot
   unintentionally fix one customer and leave the rest behind.

   Bookings are listed over the UNFILTERED set (§12): the count stays true even
   when the table is showing a filtered slice.
   ══════════════════════════════════════════════════════════════════════════ */

const OperationalFlightModal = ({ open, onClose, operationalFlightKey, onApplyToAll }) => {
  const { execute, loading } = useFlightsOnOperationalFlight();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !operationalFlightKey) return;
    let cancelled = false;
    setData(null);
    setError(null);
    execute(operationalFlightKey).then((res) => {
      if (cancelled) return;
      if (res.success) setData(res.data);
      else setError(res.message || "Failed to load bookings for this flight");
    });
    return () => { cancelled = true; };
  }, [open, operationalFlightKey, execute]);

  const flight = data?.flight;
  const bookings = data?.bookings || [];
  const summary = data?.summary;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Bookings on this flight"
      width="720px"
      footer={
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          <button
            className={styles.applyBtn}
            disabled={!bookings.length || loading}
            onClick={() => onApplyToAll(bookings)}
          >
            Apply schedule change to all {summary?.bookings || 0} booking
            {summary?.bookings === 1 ? "" : "s"}
          </button>
        </div>
      }
    >
      <div className={styles.body}>
        {loading && <div className={styles.state}>Loading bookings…</div>}
        {error && <div className={styles.error}>{error}</div>}

        {flight && (
          <>
            {/* §61 — same product-header shape used across operational modules. */}
            <div className={styles.flightHeader}>
              {flight.airlineLogo ? (
                <img src={flight.airlineLogo} alt="" className={styles.logo} />
              ) : (
                <div className={styles.logoFallback}><Plane size={18} /></div>
              )}
              <div className={styles.flightMeta}>
                <strong>
                  {flight.flightNumber} · {flight.departureAirport} → {flight.arrivalAirport}
                </strong>
                <span>
                  {flight.airline} · {formatDMY(flight.departureDate)}
                  {flight.departureCity && ` · ${flight.departureCity} → ${flight.arrivalCity}`}
                </span>
              </div>
              <div className={styles.counts}>
                <span><Users size={13} /> {summary?.travelers || 0}</span>
                <span>{summary?.bookings || 0} booking{summary?.bookings === 1 ? "" : "s"}</span>
              </div>
            </div>

            {/* §54 — bookings on one flight holding different times means an
                earlier change was applied to some of them but not all. */}
            {summary?.outOfSync && (
              <div className={styles.outOfSync}>
                <AlertTriangle size={16} />
                <div>
                  <strong>These bookings are out of sync.</strong> They hold different
                  times for the same operational flight
                  {flight.departureTimes?.length > 1 && ` (departures: ${flight.departureTimes.join(", ")})`}
                  {" "}— an earlier change was probably applied to some but not all.
                </div>
              </div>
            )}

            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>PNR</th>
                  <th>Supplier ref</th>
                  <th>Schedule</th>
                  <th>Pax</th>
                  {/* §64 — is the itinerary still flyable, and does the
                      paperwork still match? Both matter before changing it. */}
                  <th>Connection</th>
                  <th>Documents</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id}>
                    <td><strong>{b.bookingNumber}</strong></td>
                    <td className={styles.mono}>{b.pnr || "—"}</td>
                    <td className={styles.mono}>{b.supplierReference || "—"}</td>
                    <td className={styles.mono}>
                      {b.departureTime} → {b.arrivalTime}
                    </td>
                    <td>{b.travelers}</td>
                    <td>
                      {!b.connection || !b.connection.connections?.length ? (
                        <span className={styles.subtle}>direct</span>
                      ) : b.connection.status === "CONNECTION_INVALID" ? (
                        <span className={styles.badgeCancelled}>impossible</span>
                      ) : b.connection.status === "CONNECTION_WARNING" ? (
                        <span className={styles.badgeWarn}>tight</span>
                      ) : (
                        <span className={styles.badgeOk}>ok</span>
                      )}
                    </td>
                    <td>
                      {!b.documents || !b.documents.checked ? (
                        <span className={styles.subtle}>none</span>
                      ) : b.documents.outOfSync ? (
                        <span
                          className={styles.badgeCancelled}
                          title={b.documents.stale.map((d) => d.documentNumber).join(", ")}
                        >
                          {b.documents.outOfSync} stale
                        </span>
                      ) : (
                        <span className={styles.badgeOk}>in sync</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={
                          b.cancelled || b.bookingStatus === "Cancelled"
                            ? styles.badgeCancelled
                            : styles.badgeOk
                        }
                      >
                        {b.cancelled ? "Cancelled" : b.bookingStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className={styles.note}>
              Every booking is processed individually — one failure will not affect the others.
            </p>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default OperationalFlightModal;
